/**
 * Cadastro e edição de cliente — PF e PJ na mesma tela.
 *
 * O seletor de tipo troca CAMPOS, não só rótulos: PF tem nascimento e CPF
 * opcional, PJ tem razão social, nome fantasia, inscrição estadual e CNPJ
 * obrigatório. A API recusa campo do outro tipo em vez de descartar em
 * silêncio, então a tela não pode mandar os dois "por segurança".
 *
 * O cartão de crediário só aparece para quem tem `credit.enable`. Esconder é
 * conveniência de UX e não protege nada (CONSTITUICAO.md §4.1) — quem decide é
 * o porteiro. O que a tela faz de importante é OMITIR o bloco quando não pode
 * mexer nele: bloco ausente significa "não mexer", e é isso que permite ao
 * vendedor corrigir um telefone sem revogar o crédito que o gerente liberou.
 */
import { useEffect, useState, type ReactNode } from 'react'

import { Segmentado } from '../../componentes/Segmentado'
import { Campo, MensagemDeErro } from '../../componentes/Campos'
import { CampoData } from '../../componentes/CampoData'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { buscarCEP, buscarCNPJ } from '../../plataforma/auxiliares'
import { analisar, formatar } from '../../plataforma/dinheiro'
import {
  CORES_SUGERIDAS,
  ETIQUETAS_SUGERIDAS,
  NUMERACOES,
  TAMANHOS_ROUPA,
  UFS,
  criarCliente,
  salvarCliente,
  type FichaCliente,
  type PedidoCliente,
  type StatusCliente,
  type TipoCliente,
} from '../../plataforma/clientes'
import {
  cepCompleto,
  cnpjValido,
  cpfValido,
  limparCEP,
  limparCNPJ,
  limparCPF,
  limparTelefone,
  mascararCEP,
  mascararCNPJ,
  mascararCPF,
  mascararTelefone,
  telefoneCompleto,
} from '../../plataforma/mascaras'

type Rascunho = {
  tipo: TipoCliente
  nome: string
  nomeFantasia: string
  documento: string
  nascimento: string
  inscricaoEstadual: string
  telefone: string
  email: string
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
  tamanho: string
  numeracao: string
  cores: string[]
  etiquetas: string[]
  aceitaWhatsapp: boolean
  aceitaMarketing: boolean
  crediarioLiberado: boolean
  limite: string
  parcelas: number
  diaVencimento: string
  observacao: string
  status: StatusCliente
}

const VAZIO: Rascunho = {
  tipo: 'pf',
  nome: '',
  nomeFantasia: '',
  documento: '',
  nascimento: '',
  inscricaoEstadual: '',
  telefone: '',
  email: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  tamanho: '',
  numeracao: '',
  cores: [],
  etiquetas: [],
  // Consentimento não tem default esperto: ausente é NÃO (LGPD).
  aceitaWhatsapp: false,
  aceitaMarketing: false,
  crediarioLiberado: false,
  limite: '',
  parcelas: 1,
  diaVencimento: '',
  observacao: '',
  status: 'ativo',
}

function rascunhoDe(ficha: FichaCliente): Rascunho {
  return {
    tipo: ficha.tipo,
    nome: ficha.nome,
    nomeFantasia: ficha.nome_fantasia ?? '',
    documento: ficha.documento ?? '',
    nascimento: ficha.nascimento ?? '',
    inscricaoEstadual: ficha.inscricao_estadual ?? '',
    telefone: ficha.contato.telefone ?? '',
    email: ficha.contato.email ?? '',
    cep: ficha.endereco.cep ?? '',
    logradouro: ficha.endereco.logradouro ?? '',
    numero: ficha.endereco.numero ?? '',
    complemento: ficha.endereco.complemento ?? '',
    bairro: ficha.endereco.bairro ?? '',
    cidade: ficha.endereco.cidade ?? '',
    uf: ficha.endereco.uf ?? '',
    tamanho: ficha.preferencia.tamanho ?? '',
    numeracao: ficha.preferencia.numeracao ?? '',
    cores: ficha.preferencia.cores,
    etiquetas: ficha.etiquetas,
    aceitaWhatsapp: ficha.contato.aceita_whatsapp,
    aceitaMarketing: ficha.contato.aceita_marketing,
    crediarioLiberado: ficha.crediario.liberado,
    // O limite viaja em centavos e é formatado só aqui, na borda
    // (CONSTITUICAO.md §1).
    limite: ficha.crediario.liberado ? formatar(ficha.crediario.limite_centavos) : '',
    parcelas: ficha.crediario.parcelas_max,
    diaVencimento: ficha.crediario.dia_vencimento ? String(ficha.crediario.dia_vencimento) : '',
    observacao: ficha.observacao_interna ?? '',
    status: ficha.status,
  }
}

/** Erro por campo, no mesmo formato que a API devolve em `erro.campos`. */
type Erros = Record<string, string>

/**
 * Confere o que dá para conferir sem ir ao servidor.
 *
 * Não substitui a validação do backend — duplica de propósito o que faz o erro
 * aparecer no campo em que a pessoa digitou, em vez de depois de submeter um
 * formulário de vinte campos. O servidor continua sendo quem decide.
 */
function conferir(rascunho: Rascunho, podeCrediario: boolean): Erros {
  const erros: Erros = {}
  const pf = rascunho.tipo === 'pf'

  if (rascunho.nome.trim().length < 2) {
    erros.nome = pf ? 'Informe o nome completo do cliente.' : 'Informe a razão social.'
  }

  const documento = pf ? limparCPF(rascunho.documento) : limparCNPJ(rascunho.documento)
  if (!pf && documento === '') {
    erros.documento = 'Pessoa jurídica exige CNPJ.'
  } else if (documento !== '') {
    const valido = pf ? cpfValido(documento) : cnpjValido(documento)
    if (!valido) erros.documento = pf ? 'CPF inválido. Confira os dígitos.' : 'CNPJ inválido. Confira os dígitos.'
  }

  if (rascunho.telefone !== '' && !telefoneCompleto(rascunho.telefone)) {
    erros.telefone = 'Telefone com DDD: 10 dígitos no fixo, 11 no celular.'
  }
  if (rascunho.email !== '' && !/^[^\s@]+@[^\s@]+$/.test(rascunho.email.trim())) {
    erros.email = 'E-mail inválido.'
  }
  if (rascunho.cep !== '' && !cepCompleto(rascunho.cep)) {
    erros.cep = 'CEP tem 8 dígitos.'
  }

  if (podeCrediario && rascunho.crediarioLiberado) {
    const limite = analisar(rascunho.limite)
    if (limite === null || limite <= 0) {
      erros['crediario.limite_centavos'] =
        'Crediário liberado sem limite é crédito infinito. Informe o limite.'
    }
    if (documento === '') {
      erros.documento = 'Para liberar crediário, informe o CPF ou CNPJ do cliente.'
    }
    if (!enderecoCompleto(rascunho)) {
      erros.cep = 'Para liberar crediário, preencha CEP, rua, número, bairro, cidade e UF.'
    }
  }

  return erros
}

function enderecoCompleto(r: Rascunho): boolean {
  return (
    cepCompleto(r.cep) &&
    r.logradouro.trim() !== '' &&
    r.numero.trim() !== '' &&
    r.bairro.trim() !== '' &&
    r.cidade.trim() !== '' &&
    r.uf !== ''
  )
}

/**
 * Monta o corpo do pedido.
 *
 * `podeCrediario` decide se o bloco de crediário VAI. Omitir quando não pode é
 * o que faz a API preservar o que está gravado — mandar zerado revogaria o
 * crédito a cada edição de contato.
 */
function pedidoDe(rascunho: Rascunho, podeCrediario: boolean): PedidoCliente {
  const pf = rascunho.tipo === 'pf'
  const semVazio = (texto: string) => (texto.trim() === '' ? undefined : texto.trim())

  const pedido: PedidoCliente = {
    tipo: rascunho.tipo,
    nome: rascunho.nome.trim(),
    // Campo do outro tipo NÃO viaja: a API recusa em vez de descartar.
    nome_fantasia: pf ? undefined : semVazio(rascunho.nomeFantasia),
    inscricao_estadual: pf ? undefined : semVazio(rascunho.inscricaoEstadual),
    nascimento: pf ? semVazio(rascunho.nascimento) : undefined,
    documento: (pf ? limparCPF(rascunho.documento) : limparCNPJ(rascunho.documento)) || undefined,
    email: semVazio(rascunho.email.toLowerCase()),
    telefone: limparTelefone(rascunho.telefone) || undefined,
    cep: limparCEP(rascunho.cep) || undefined,
    logradouro: semVazio(rascunho.logradouro),
    numero: semVazio(rascunho.numero),
    complemento: semVazio(rascunho.complemento),
    bairro: semVazio(rascunho.bairro),
    cidade: semVazio(rascunho.cidade),
    uf: semVazio(rascunho.uf),
    tamanho_preferido: semVazio(rascunho.tamanho),
    numeracao_preferida: semVazio(rascunho.numeracao),
    cores_preferidas: rascunho.cores,
    etiquetas: rascunho.etiquetas,
    aceita_whatsapp: rascunho.aceitaWhatsapp,
    aceita_marketing: rascunho.aceitaMarketing,
    observacao_interna: semVazio(rascunho.observacao),
    status: rascunho.status,
  }

  if (podeCrediario) {
    const limite = rascunho.crediarioLiberado ? (analisar(rascunho.limite) ?? 0) : 0
    pedido.crediario = {
      liberado: rascunho.crediarioLiberado,
      limite_centavos: limite,
      parcelas_max: rascunho.crediarioLiberado ? rascunho.parcelas : 1,
      dia_vencimento:
        rascunho.crediarioLiberado && rascunho.diaVencimento !== ''
          ? Number(rascunho.diaVencimento)
          : undefined,
    }
  }

  return pedido
}

/**
 * Id do `<form>` — é o que liga o botão "Salvar" do CABEÇALHO (fora desta
 * árvore de componentes) a este formulário, via o atributo HTML `form`. O
 * protótipo (`Lumma Fit Cadastro Cliente e Fornecedor.dc.html`) põe Cancelar
 * e Salvar no cabeçalho da página, não numa coluna lateral — `form="..."` é
 * o jeito nativo de um botão fora do `<form>` ainda disparar o submit dele.
 */
export const ID_FORMULARIO_CLIENTE = 'formulario-cliente'

export function FormularioCliente({
  ficha,
  podeCrediario,
  aoSalvar,
  aoEstadoMudar,
}: {
  /** Ficha existente em modo edição; ausente em cadastro novo. */
  ficha?: FichaCliente
  podeCrediario: boolean
  aoSalvar: (salva: FichaCliente) => void
  /** Avisa o cabeçalho (dono dos botões "Cancelar"/"Salvar") se está enviando agora. */
  aoEstadoMudar?: (enviando: boolean) => void
}) {
  const [rascunho, setRascunho] = useState<Rascunho>(ficha ? rascunhoDe(ficha) : VAZIO)
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    aoEstadoMudar?.(enviando)
  }, [enviando, aoEstadoMudar])

  const [buscandoCEP, setBuscandoCEP] = useState(false)
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false)

  const pf = rascunho.tipo === 'pf'
  const alterar = <C extends keyof Rascunho>(campo: C, valor: Rascunho[C]) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }))

  /**
   * CEP completo (8 dígitos) busca o endereço via ViaCEP e substitui
   * rua/bairro/cidade/UF — é o próprio CEP que muda esses campos, então
   * preservar o que estava lá seria manter o endereço do CEP antigo. Número e
   * complemento o ViaCEP não conhece, e ficam como a pessoa digitou.
   */
  const aoMudarCEP = (texto: string) => {
    alterar('cep', texto)
    if (limparCEP(texto).length !== 8) return

    setBuscandoCEP(true)
    buscarCEP(texto)
      .then((endereco) => {
        if (!endereco) return
        setRascunho((atual) => ({
          ...atual,
          logradouro: endereco.logradouro || atual.logradouro,
          bairro: endereco.bairro || atual.bairro,
          cidade: endereco.cidade || atual.cidade,
          uf: endereco.uf || atual.uf,
        }))
      })
      .finally(() => setBuscandoCEP(false))
  }

  /**
   * CNPJ completo (14 dígitos), só em pessoa jurídica, busca os dados
   * cadastrais na Receita (via BrasilAPI). Aqui é o oposto do CEP: só
   * preenche campo VAZIO. A razão social que a pessoa já digitou pode ser
   * "Box Alpha" enquanto a Receita registra "Alpha Treinamentos Ltda" — os
   * dois estão certos, e substituir o que já foi escrito apagaria uma escolha
   * deliberada por causa de uma sugestão.
   */
  const aoMudarDocumento = (texto: string) => {
    alterar('documento', texto)
    if (pf || limparCNPJ(texto).length !== 14) return

    setBuscandoCNPJ(true)
    buscarCNPJ(texto)
      .then((dados) => {
        if (!dados) return
        setRascunho((atual) => ({
          ...atual,
          nome: atual.nome.trim() === '' ? dados.razao_social : atual.nome,
          nomeFantasia: atual.nomeFantasia.trim() === '' ? dados.nome_fantasia : atual.nomeFantasia,
          telefone: atual.telefone.trim() === '' ? (dados.telefone ?? '') : atual.telefone,
          email: atual.email.trim() === '' ? (dados.email ?? '') : atual.email,
          cep: atual.cep.trim() === '' ? dados.cep : atual.cep,
          logradouro: atual.logradouro.trim() === '' ? dados.logradouro : atual.logradouro,
          numero: atual.numero.trim() === '' ? dados.numero : atual.numero,
          complemento: atual.complemento.trim() === '' ? dados.complemento : atual.complemento,
          bairro: atual.bairro.trim() === '' ? dados.bairro : atual.bairro,
          cidade: atual.cidade.trim() === '' ? dados.cidade : atual.cidade,
          uf: atual.uf.trim() === '' ? dados.uf : atual.uf,
        }))
      })
      .finally(() => setBuscandoCNPJ(false))
  }

  const alternarNaLista = (campo: 'cores' | 'etiquetas', valor: string) =>
    setRascunho((atual) => ({
      ...atual,
      [campo]: atual[campo].includes(valor)
        ? atual[campo].filter((item) => item !== valor)
        : [...atual[campo], valor],
    }))

  async function enviar() {
    const encontrados = conferir(rascunho, podeCrediario)
    if (Object.keys(encontrados).length > 0) {
      setErros(encontrados)
      setAviso('Confira os campos destacados.')
      return
    }

    setErros({})
    setAviso('')
    setEnviando(true)
    try {
      const pedido = pedidoDe(rascunho, podeCrediario)
      const salva = ficha ? await salvarCliente(ficha.codigo, pedido) : await criarCliente(pedido)
      aoSalvar(salva)
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível salvar o cliente.')
        return
      }
      // A API devolve erro POR CAMPO — é isso que permite destacar o input em
      // vez de mostrar um aviso solto no topo de vinte campos.
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <form
      id={ID_FORMULARIO_CLIENTE}
      onSubmit={(evento) => {
        evento.preventDefault()
        void enviar()
      }}
      className="grid w-full items-start gap-4 xl:grid-cols-[minmax(0,1fr)_300px]"
    >
      {/*
        Full-width, logo abaixo do cabeçalho onde ficam os botões "Cancelar" e
        "Salvar" — é para onde o olho vai depois de clicar "Salvar". O aviso
        que já existia lá embaixo, perto do checklist de crediário, exigia
        rolar a tela inteira para descobrir por que nada aconteceu.
      */}
      {aviso && (
        <div
          role="alert"
          className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto xl:col-span-2"
        >
          {aviso}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <Cartao
          titulo="Identificação"
          subtitulo="Dados usados na nota e na ficha do cliente"
        >
          <div className="flex flex-col gap-4">
            <div>
              <span className="mb-[7px] block text-[13px] font-semibold text-texto">
                Tipo de cliente
              </span>
              {ficha ? (
                // Trocar o tipo depois de criado limparia CNPJ/CPF, nascimento
                // e nome fantasia (são campos exclusivos de um tipo) — na
                // edição isso é perda de dado por engano, não uma correção.
                // Pessoa física que virou jurídica (ou o contrário) é um
                // cliente novo, não uma edição.
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex items-center rounded-[6px] bg-marca-suave px-3 py-2 text-[13.5px] font-semibold text-marca-forte">
                    {pf ? 'Pessoa física' : 'Pessoa jurídica'}
                  </span>
                  <span className="text-xs text-texto-suave">
                    Não muda depois de criado — cadastre um cliente novo se for o caso.
                  </span>
                </div>
              ) : (
                <Segmentado
                  opcoes={[
                    { chave: 'pf', rotulo: 'Pessoa física' },
                    { chave: 'pj', rotulo: 'Pessoa jurídica' },
                  ]}
                  valor={rascunho.tipo}
                  aoTrocar={(valor) => {
                    // Trocar o tipo limpa o documento: um CPF de 11 dígitos não
                    // é um CNPJ, e manter o texto faria a máscara mostrar um
                    // número que a API vai recusar.
                    setRascunho((atual) => ({
                      ...atual,
                      tipo: valor,
                      documento: '',
                      nascimento: '',
                      nomeFantasia: '',
                      inscricaoEstadual: '',
                    }))
                    setErros({})
                  }}
                />
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo={pf ? 'CPF' : 'CNPJ'}
                inputMode={pf ? 'numeric' : 'text'}
                value={pf ? mascararCPF(rascunho.documento) : mascararCNPJ(rascunho.documento)}
                onChange={(evento) => aoMudarDocumento(evento.target.value)}
                placeholder={pf ? '000.000.000-00' : '00.000.000/0000-00'}
                erro={erros.documento}
                acessorio={buscandoCNPJ ? <IndicadorBusca /> : undefined}
                dica={
                  pf
                    ? 'Opcional na venda no balcão'
                    : 'Obrigatório na nota com destinatário — preenche o resto sozinho'
                }
              />

              {pf ? (
                <CampoData
                  rotulo="Data de nascimento"
                  value={rascunho.nascimento}
                  onChange={(iso) => alterar('nascimento', iso)}
                  erro={erros.nascimento}
                  dica="Usada na campanha de aniversário"
                />
              ) : (
                <Campo
                  rotulo="Inscrição estadual"
                  value={rascunho.inscricaoEstadual}
                  onChange={(evento) => alterar('inscricaoEstadual', evento.target.value)}
                  placeholder="Isento"
                  erro={erros.inscricao_estadual}
                />
              )}
            </div>

            <Campo
              rotulo={pf ? 'Nome completo' : 'Razão social'}
              value={rascunho.nome}
              onChange={(evento) => alterar('nome', evento.target.value)}
              placeholder={pf ? 'Marina Reis' : 'Box Alpha Treinamento Ltda'}
              erro={erros.nome}
            />

            {!pf && (
              <Campo
                rotulo="Nome fantasia"
                value={rascunho.nomeFantasia}
                onChange={(evento) => alterar('nomeFantasia', evento.target.value)}
                placeholder="Box Alpha"
                erro={erros.nome_fantasia}
              />
            )}
          </div>
        </Cartao>

        <Cartao titulo="Contato" subtitulo="O WhatsApp é o canal padrão para cupom e campanha">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Telefone / WhatsApp"
                inputMode="numeric"
                value={mascararTelefone(rascunho.telefone)}
                onChange={(evento) => alterar('telefone', evento.target.value)}
                placeholder="(11) 90000-0000"
                erro={erros.telefone}
              />
              <Campo
                rotulo="E-mail"
                type="email"
                value={rascunho.email}
                onChange={(evento) => alterar('email', evento.target.value)}
                placeholder="cliente@email.com"
                erro={erros.email}
              />
            </div>

            {/*
              Dois consentimentos, dois interruptores. Não existe um controle
              que ligue os dois: cupom da própria compra e campanha são
              autorizações diferentes, e tratar como uma só é o que gera
              reclamação de titular (LGPD).
            */}
            <div>
              <LinhaInterruptor
                rotulo="Enviar cupom por WhatsApp"
                dica="Dispensa impressão no PDV"
                ligado={rascunho.aceitaWhatsapp}
                aoTrocar={(valor) => alterar('aceitaWhatsapp', valor)}
              />
              <LinhaInterruptor
                rotulo="Aceita receber campanhas"
                dica="Necessário consentimento — LGPD"
                ligado={rascunho.aceitaMarketing}
                aoTrocar={(valor) => alterar('aceitaMarketing', valor)}
              />
            </div>
          </div>
        </Cartao>

        <Cartao titulo="Endereço" subtitulo="Necessário para entrega e para nota com destinatário">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Campo
                rotulo="CEP"
                inputMode="numeric"
                value={mascararCEP(rascunho.cep)}
                onChange={(evento) => aoMudarCEP(evento.target.value)}
                placeholder="00000-000"
                erro={erros.cep}
                acessorio={buscandoCEP ? <IndicadorBusca /> : undefined}
              />
              <Campo
                rotulo="Número"
                inputMode="numeric"
                value={rascunho.numero}
                onChange={(evento) => alterar('numero', evento.target.value)}
                placeholder="000"
                erro={erros.numero}
              />
              <Campo
                rotulo="Complemento"
                value={rascunho.complemento}
                onChange={(evento) => alterar('complemento', evento.target.value)}
                placeholder="Apto, bloco"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Rua"
                value={rascunho.logradouro}
                onChange={(evento) => alterar('logradouro', evento.target.value)}
                placeholder="Av. Paulista"
                erro={erros.logradouro}
              />
              <Campo
                rotulo="Bairro"
                value={rascunho.bairro}
                onChange={(evento) => alterar('bairro', evento.target.value)}
                placeholder="Bela Vista"
                erro={erros.bairro}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Cidade"
                value={rascunho.cidade}
                onChange={(evento) => alterar('cidade', evento.target.value)}
                placeholder="São Paulo"
                erro={erros.cidade}
              />
              <div>
                <label
                  htmlFor="cliente-uf"
                  className="mb-2 block text-[13px] font-semibold text-texto"
                >
                  UF
                </label>
                <select
                  id="cliente-uf"
                  className="campo"
                  aria-invalid={erros.uf ? true : undefined}
                  value={rascunho.uf}
                  onChange={(evento) => alterar('uf', evento.target.value)}
                >
                  <option value="">Selecione</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                {erros.uf && <MensagemDeErro>{erros.uf}</MensagemDeErro>}
              </div>
            </div>
          </div>
        </Cartao>

        <Cartao
          titulo="Preferências de grade"
          subtitulo="O PDV sugere estes tamanhos primeiro nas próximas vendas"
        >
          <div className="flex flex-col gap-[18px]">
            <GrupoDePills
              rotulo="Tamanho de roupa"
              opcoes={TAMANHOS_ROUPA}
              selecionada={rascunho.tamanho}
              aoTrocar={(valor) => alterar('tamanho', valor)}
            />
            <GrupoDePills
              rotulo="Numeração de tênis"
              opcoes={NUMERACOES}
              selecionada={rascunho.numeracao}
              aoTrocar={(valor) => alterar('numeracao', valor)}
            />
            <div>
              <span className="mb-2.5 block text-[13px] font-semibold text-texto">
                Cores preferidas
              </span>
              <div className="flex flex-wrap gap-2">
                {CORES_SUGERIDAS.map((cor) => {
                  const ativa = rascunho.cores.includes(cor.nome)
                  return (
                    <button
                      key={cor.nome}
                      type="button"
                      aria-pressed={ativa}
                      onClick={() => alternarNaLista('cores', cor.nome)}
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13.5px] font-semibold ${
                        ativa
                          ? 'border-marca bg-marca-suave text-marca-forte'
                          : 'border-borda bg-superficie text-tinta'
                      }`}
                    >
                      <span
                        aria-hidden
                        className="h-4 w-4 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                        style={{ background: cor.hex }}
                      />
                      {cor.nome}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </Cartao>

        {podeCrediario && (
          <Cartao titulo="Crediário" subtitulo="Vale para as vendas a prazo feitas no balcão">
            <div>
              <LinhaInterruptor
                rotulo="Liberar crediário para este cliente"
                dica="Exige documento e endereço completos"
                ligado={rascunho.crediarioLiberado}
                aoTrocar={(valor) => alterar('crediarioLiberado', valor)}
              />

              {rascunho.crediarioLiberado && (
                <div className="mt-[18px] grid gap-4 sm:grid-cols-3">
                  <div>
                    <label
                      htmlFor="cliente-limite"
                      className="mb-2 block text-[13px] font-semibold text-texto"
                    >
                      Limite de crédito
                    </label>
                    {/*
                      `composto`: a borda e o anel de foco são do INVÓLUCRO,
                      via :focus-within. O input de dentro não tem borda para
                      colorir, e pintar o anel à mão faria o campo parecer
                      focado sempre.
                    */}
                    <div
                      className="composto"
                      aria-invalid={erros['crediario.limite_centavos'] ? true : undefined}
                    >
                      <span className="text-[13px] text-texto-suave">R$</span>
                      <input
                        id="cliente-limite"
                        className="tabular"
                        inputMode="decimal"
                        value={rascunho.limite}
                        onChange={(evento) => alterar('limite', evento.target.value)}
                        placeholder="1.500,00"
                        aria-invalid={erros['crediario.limite_centavos'] ? true : undefined}
                      />
                    </div>
                    {erros['crediario.limite_centavos'] && (
                      <MensagemDeErro>{erros['crediario.limite_centavos']}</MensagemDeErro>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cliente-parcelas"
                      className="mb-2 block text-[13px] font-semibold text-texto"
                    >
                      Parcelas máximas
                    </label>
                    <select
                      id="cliente-parcelas"
                      className="campo tabular"
                      aria-invalid={erros['crediario.parcelas_max'] ? true : undefined}
                      value={rascunho.parcelas}
                      onChange={(evento) => alterar('parcelas', Number(evento.target.value))}
                    >
                      {[1, 2, 3, 4, 6, 8, 10, 12, 18, 24].map((n) => (
                        <option key={n} value={n}>
                          {n}x
                        </option>
                      ))}
                    </select>
                    {erros['crediario.parcelas_max'] && (
                      <MensagemDeErro>{erros['crediario.parcelas_max']}</MensagemDeErro>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cliente-vencimento"
                      className="mb-2 block text-[13px] font-semibold text-texto"
                    >
                      Dia de vencimento
                    </label>
                    <select
                      id="cliente-vencimento"
                      className="campo tabular"
                      aria-invalid={erros['crediario.dia_vencimento'] ? true : undefined}
                      value={rascunho.diaVencimento}
                      onChange={(evento) => alterar('diaVencimento', evento.target.value)}
                    >
                      <option value="">Sem dia fixo</option>
                      {/* Até 28: vencimento no dia 30 não existe em fevereiro,
                          e a parcela cairia num dia diferente do combinado. */}
                      {[5, 10, 15, 20, 25, 28].map((dia) => (
                        <option key={dia} value={dia}>
                          {dia}
                        </option>
                      ))}
                    </select>
                    {erros['crediario.dia_vencimento'] && (
                      <MensagemDeErro>{erros['crediario.dia_vencimento']}</MensagemDeErro>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Cartao>
        )}

        <Cartao
          titulo="Etiquetas e observações"
          subtitulo="Etiquetas alimentam campanhas e filtros na listagem"
        >
          <div className="flex flex-col gap-[18px]">
            <div className="flex flex-wrap gap-2">
              {ETIQUETAS_SUGERIDAS.map((etiqueta) => {
                const ativa = rascunho.etiquetas.includes(etiqueta)
                return (
                  <button
                    key={etiqueta}
                    type="button"
                    aria-pressed={ativa}
                    onClick={() => alternarNaLista('etiquetas', etiqueta)}
                    className={`rounded-full border px-3.5 py-2 text-[13.5px] font-semibold ${
                      ativa
                        ? 'border-marca bg-marca-suave text-marca-forte'
                        : 'border-borda bg-superficie text-tinta'
                    }`}
                  >
                    {etiqueta}
                  </button>
                )
              })}
            </div>

            <div>
              <label
                htmlFor="cliente-observacao"
                className="mb-2 block text-[13px] font-semibold text-texto"
              >
                Observações internas
              </label>
              <textarea
                id="cliente-observacao"
                value={rascunho.observacao}
                onChange={(evento) => alterar('observacao', evento.target.value.slice(0, 1000))}
                placeholder="Treina no Box Alpha, prefere peças de compressão."
                className="campo h-[80px] resize-none py-2.5"
              />
              <p className="mt-1.5 text-xs text-texto-fraco">
                Visível só para a equipe, nunca no cupom
              </p>
            </div>

            {ficha && (
              <LinhaInterruptor
                rotulo="Cliente ativo"
                dica="Inativos saem das listas sem sair da base — a venda é documento fiscal"
                ligado={rascunho.status === 'ativo'}
                aoTrocar={(valor) => alterar('status', valor ? 'ativo' : 'inativo')}
              />
            )}
          </div>
        </Cartao>
      </div>

      <div className="flex flex-col gap-4 xl:sticky xl:top-0">
        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="mb-3.5 font-titulo text-[15px] font-semibold text-tinta">Resumo</h2>
          <LinhaResumo rotulo="Tipo" valor={pf ? 'Pessoa física' : 'Pessoa jurídica'} />
          <LinhaResumo rotulo="Tamanho" valor={rascunho.tamanho || '—'} />
          <LinhaResumo rotulo="Numeração" valor={rascunho.numeracao || '—'} />
          <LinhaResumo
            rotulo="Cores"
            valor={rascunho.cores.length ? `${rascunho.cores.length} escolhidas` : '—'}
          />
          <LinhaResumo
            rotulo="Etiquetas"
            valor={rascunho.etiquetas.length ? String(rascunho.etiquetas.length) : '—'}
          />
          {podeCrediario && (
            <LinhaResumo
              rotulo="Crediário"
              valor={rascunho.crediarioLiberado ? 'Liberado' : 'Bloqueado'}
            />
          )}
        </div>

        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="font-titulo text-[15px] font-semibold text-tinta">Para vender a prazo</h2>
          <p className="mt-1 mb-3 text-[12.5px] text-texto-suave">
            O crediário só libera com estes dados
          </p>
          <ItemChecklist
            rotulo={pf ? 'CPF informado' : 'CNPJ informado'}
            pronto={(pf ? limparCPF(rascunho.documento) : limparCNPJ(rascunho.documento)) !== ''}
          />
          <ItemChecklist rotulo="Telefone com DDD" pronto={telefoneCompleto(rascunho.telefone)} />
          <ItemChecklist rotulo="Endereço completo" pronto={enderecoCompleto(rascunho)} />
        </div>

        <div className="flex gap-2.5 rounded-sistema border border-marca-borda bg-marca-fundo-2 px-3.5 py-3">
          <Icone nome="shield" tamanho={19} className="text-marca" />
          <p className="text-[12.5px] leading-[1.55] text-marca-forte">
            Sem consentimento de campanha, o cliente recebe apenas o cupom da própria compra.
          </p>
        </div>

      </div>
    </form>
  )
}

/* ------------------------------------------------------------- peças */

function Cartao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string
  subtitulo: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
      <p className="mt-[2px] mb-5 text-[12.5px] text-texto-suave">{subtitulo}</p>
      {children}
    </section>
  )
}

/**
 * Interruptor de 42×24 com transição de 160ms — spec 01, seção "Controles".
 *
 * É um `button` com `role="switch"` e `aria-checked`, e não uma div clicável:
 * consentimento de LGPD precisa ser anunciado e alcançável por teclado.
 */
function LinhaInterruptor({
  rotulo,
  dica,
  ligado,
  aoTrocar,
}: {
  rotulo: string
  dica?: string
  ligado: boolean
  aoTrocar: (valor: boolean) => void
}) {
  return (
    <div className="flex items-center gap-3.5 border-t border-divisoria py-2.5 first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-tinta">{rotulo}</div>
        {dica && <div className="mt-0.5 text-xs text-texto-suave">{dica}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={ligado}
        aria-label={rotulo}
        onClick={() => aoTrocar(!ligado)}
        className={`h-6 w-[42px] shrink-0 rounded-full p-[3px] transition-colors duration-150 ${
          ligado ? 'bg-marca' : 'bg-inativo'
        }`}
      >
        <span
          aria-hidden
          className="block h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform duration-150"
          style={{ transform: ligado ? 'translateX(18px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  )
}

function GrupoDePills({
  rotulo,
  opcoes,
  selecionada,
  aoTrocar,
}: {
  rotulo: string
  opcoes: string[]
  selecionada: string
  aoTrocar: (valor: string) => void
}) {
  return (
    <div>
      <span className="mb-2.5 block text-[13px] font-semibold text-texto">{rotulo}</span>
      <div className="flex flex-wrap gap-2">
        {opcoes.map((opcao) => {
          const ativa = opcao === selecionada
          return (
            <button
              key={opcao}
              type="button"
              aria-pressed={ativa}
              // Clicar na opção ativa desmarca: preferência é opcional, e sem
              // isto não haveria como voltar atrás depois de escolher.
              onClick={() => aoTrocar(ativa ? '' : opcao)}
              className={`tabular min-w-[48px] rounded-full border px-3.5 py-2 text-center text-[13.5px] font-semibold ${
                ativa
                  ? 'border-marca bg-marca-suave text-marca-forte'
                  : 'border-borda bg-superficie text-tinta'
              }`}
            >
              {opcao}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function LinhaResumo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3 border-t border-divisoria py-2.5 text-[13px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular text-right font-semibold text-tinta">{valor}</span>
    </div>
  )
}

function ItemChecklist({ rotulo, pronto }: { rotulo: string; pronto: boolean }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span
        aria-hidden
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-[1.5px] ${
          pronto ? 'border-marca bg-marca text-white' : 'border-inativo bg-superficie'
        }`}
      >
        {pronto && <Icone nome="check" tamanho={14} peso={600} />}
      </span>
      <span className={`text-[13.5px] ${pronto ? 'text-tinta' : 'text-texto-suave'}`}>
        {rotulo}
      </span>
      <span className="sr-only">{pronto ? 'pronto' : 'falta preencher'}</span>
    </div>
  )
}

/**
 * Selo "Buscando…" ao lado do rótulo do campo, com o ícone girando.
 *
 * A busca de CEP/CNPJ costuma responder rápido demais para o texto abaixo do
 * campo (a `dica`) chamar atenção — a pessoa já mudou de campo quando ela
 * aparece e some. Ao lado do rótulo, no mesmo lugar em que o olho já está
 * porque acabou de digitar ali, é onde dá para perceber.
 */
function IndicadorBusca() {
  return (
    <span
      role="status"
      className="flex items-center gap-1.5 text-[12px] font-medium text-marca"
    >
      <Icone nome="sync" tamanho={13} className="animate-spin" />
      Buscando…
    </span>
  )
}
